import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Eye, Sparkles } from 'lucide-react';

const EyebrowPatterns = () => {
  const { eyebrowPatterns, features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();

  // Only show on eyebrow transplant pages
  if (!features?.showEyebrowPatterns || !eyebrowPatterns?.length) {
    return null;
  }

  // Translations for eyebrow pattern titles and descriptions
  const patternTranslations = {
    'Kahle Augenbrauen': { en: 'Bare Eyebrows' },
    'Undichte Augenbrauen': { en: 'Sparse Eyebrows' },
    'Microblading-Alternative': { en: 'Microblading Alternative' },
  };

  const descriptionTranslations = {
    'Vollständig fehlende oder extrem dünne Augenbrauen, die eine komplette Rekonstruktion erfordern': {
      en: 'Completely missing or extremely thin eyebrows requiring complete reconstruction'
    },
    'Lücken oder dünne Stellen in den Augenbrauen, die eine Verdichtung benötigen': {
      en: 'Gaps or thin spots in the eyebrows that need densification'
    },
    'Natürliche und dauerhafte Alternative zu Microblading für definierte Augenbrauen': {
      en: 'Natural and permanent alternative to microblading for defined eyebrows'
    },
  };

  const getTranslatedTitle = (title) => {
    if (language === 'de') return title;
    return patternTranslations[title]?.en || title;
  };

  const getTranslatedDescription = (desc) => {
    if (language === 'de') return desc;
    return descriptionTranslations[desc]?.en || desc;
  };

  return (
    <section id="augenbrauenmuster" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0f0f0f]' : 'bg-[#f8f9fa]'
    }`}>
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute top-1/4 right-0 w-96 h-96 rounded-full blur-3xl ${
          isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
        }`} />
        <div className={`absolute bottom-1/4 left-0 w-80 h-80 rounded-full blur-3xl ${
          isDark ? 'bg-[#5A8BA6]/5' : 'bg-[#7BA7C2]/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 ${
            isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/20 text-[#5A8BA6]'
          }`}>
            <Eye className="w-4 h-4" />
            <span className="text-sm font-medium">
              {language === 'de' ? 'Behandlungsoptionen' : 'Treatment Options'}
            </span>
          </div>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Typische ' : 'Typical '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {language === 'de' ? 'Augenbrauenmuster' : 'Eyebrow Patterns'}
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'de' 
              ? 'Jedes Augenbrauenmuster erfordert einen individuellen Behandlungsansatz. Hier finden Sie einen Überblick über die häufigsten Muster und die entsprechenden Behandlungsoptionen.'
              : 'Each eyebrow pattern requires an individual treatment approach. Here you will find an overview of the most common patterns and corresponding treatment options.'}
          </p>
        </div>

        {/* Eyebrow Patterns Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {eyebrowPatterns.map((pattern) => (
            <div
              key={pattern.id}
              className="group relative h-full"
            >
              {/* Card */}
              <div className={`rounded-2xl overflow-hidden border transition-all duration-500 h-full flex flex-col card-lift ${
                isDark 
                  ? 'bg-[#0f0f0f] border-white/5 hover:border-[#7BA7C2]/30'
                  : 'bg-white border-gray-200 hover:border-[#5A8BA6]/30'
              }`}>
                {/* Image with overlay */}
                <div className="relative h-56 overflow-hidden flex-shrink-0">
                  <img
                    src={pattern.image}
                    alt={`${getTranslatedTitle(pattern.title)} Augenbrauentransplantation - Dion Hair Clinic`}
                    loading="lazy"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${
                    isDark ? 'from-[#0f0f0f] via-transparent' : 'from-white/80 via-transparent'
                  } to-transparent`} />
                  
                  {/* Title overlay */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold text-white group-hover:text-[#9BBFD9] transition-colors duration-300">
                      {getTranslatedTitle(pattern.title)}
                    </h3>
                  </div>
                </div>

                {/* Content */}
                <div className={`p-6 border-t flex flex-col flex-grow ${
                  isDark ? 'border-white/5' : 'border-gray-100'
                }`}>
                  <p className={`text-sm leading-relaxed mb-4 flex-grow ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {getTranslatedDescription(pattern.description)}
                  </p>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mt-auto">
                    <div className={`p-3 rounded-xl text-center ${
                      isDark ? 'bg-white/5' : 'bg-gray-50'
                    }`}>
                      <div className={`text-lg font-bold ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                        {pattern.grafts}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {language === 'de' ? 'Typische Grafts' : 'Typical Grafts'}
                      </div>
                    </div>
                    <div className={`p-3 rounded-xl text-center ${
                      isDark ? 'bg-white/5' : 'bg-gray-50'
                    }`}>
                      <div className={`text-lg font-bold ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                        {language === 'de' ? pattern.sessions : pattern.sessions.replace('Sitzung', 'Session').replace('Sitzungen', 'Sessions')}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {language === 'de' ? 'Behandlung' : 'Treatment'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EyebrowPatterns;
